/**
 * Main logic for the uploader component.
 *
 * Future upgrades:
 *  - allow functionality to handle multiple datasets.
 */
// Shapfile support
import proj4 from 'proj4';
import {read as readShp} from 'shapefile';

// KML and GPX support
import {DOMParser} from '@xmldom/xmldom';
import {gpx, kml} from '@tmcw/togeojson';

// WKT support
import WktParse from 'wellknown';

// ZIP support
import JSZip from 'jszip';

import {getGeometryTypes, isGeometryValid, transformGeometry} from './geom.js';

/** These extensions indicate a unique dataset.
 *  In the case where multiple "datasets" are asked
 *  to be imported, the library will add a warning to the collection.
 */
export const DATASET_EXTENSIONS = [
  'shp',
  'wkt',
  'json',
  'geojson',
  'gpx',
  'kml',
];

export const EXTENSIONS = DATASET_EXTENSIONS.concat([
  'dbf',
  'shx',
  'prj',
  'zip',
  'kmz',
]);

export const ERRORS = {
  MISMATCHED_PRJ: 'The PRJ and SHP files do not match each other.',
  UNRECOGNIZED: 'The file extension is unrecognized.',
  NODATA: 'Failed to find any parseable data.',
  CORRUPT: 'Shapefile is missing or corrupt.',
  BAD_PRJ: 'This is an unsupported projection.',
};

function hasFileWithSuffix(files, suffix) {
  const regexp = new RegExp(`\.${suffix}$`, 'i');
  const reOSX = new RegExp('^__MACOSX/');
  return files
    .filter(file => !file.name.match(reOSX))
    .filter(file => file.name.match(regexp));
}

function removeSuffix(filename) {
  // filenames can have multiple '.'s do not assume
  //  you can just split it by '.' and check [1].
  return filename.split('.').slice(0, -1).join('.');
}

function getExtension(filename) {
  return filename.split('.').pop().toLowerCase();
}

export function projTransform(prjText, collection) {
  const transformation = proj4(prjText, 'EPSG:4326');
  return {
    ...collection,
    features: collection.features.map(feature => ({
      ...feature,
      geometry: transformGeometry(transformation, feature.geometry),
    })),
  };
}

function parseShapefile(files) {
  return new Promise((resolve, reject) => {
    const hasShp = hasFileWithSuffix(files, 'shp');
    const hasPrj = hasFileWithSuffix(files, 'prj');
    const hasDbf = hasFileWithSuffix(files, 'dbf');

    // assume the shapefile is in 4326
    hasShp[0]
      .arrayBuffer()
      .then(bytes => {
        if (hasDbf.length > 0) {
          return hasDbf[0].arrayBuffer().then(dbfBytes => {
            return readShp(bytes, dbfBytes);
          });
        } else {
          return readShp(bytes);
        }
      })
      .then(shapes => {
        if (hasPrj[0]) {
          const matchingPrj =
            removeSuffix(hasShp[0].name) === removeSuffix(hasPrj[0].name);
          if (matchingPrj) {
            return hasPrj[0]
              .text()
              .then(prjText => projTransform(prjText, shapes))
              .catch(() => {
                reject(new Error(ERRORS.BAD_PRJ));
              });
          } else {
            reject(new Error(ERRORS.MISMATCHED_PRJ));
          }
        } else {
          return shapes;
        }
      })
      .then(collection => {
        delete collection.bbox;
        return collection;
      })
      .then(collection => {
        resolve(collection);
      })
      .catch(() => {
        reject(new Error(ERRORS.CORRUPT));
      });
  });
}

export function singleFileParser(file) {
  const suffix = getExtension(file.name);
  if (suffix === 'geojson' || suffix === 'json') {
    // this is all promise-like so no need to wrap.
    return file.text().then(text => {
      const json = JSON.parse(text);
      if (json.type === 'Feature') {
        return {
          type: 'FeatureCollection',
          features: [json],
        };
      } else if (json.type === 'FeatureCollection') {
        return json;
      } else if (json.type === 'Polygon' || json.type === 'MultiPolygon') {
        return {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              properties: {},
              geometry: json,
            },
          ],
        };
      } else {
        throw new Error(ERRORS.CORRUPT);
      }
    });
  } else if (suffix === 'kml' || suffix === 'gpx') {
    return file
      .text()
      .then(text => {
        const parser = new DOMParser();
        return parser.parseFromString(text);
      })
      .then(xmldom => {
        if (suffix === 'kml') {
          // styles can get weird, removing them cleans up the
          //  data for the parser.
          const stripTags = ['Style', 'StyleMap'];
          stripTags.forEach(tag => {
            const removeList = xmldom.getElementsByTagName(tag);
            for (let i = 0, ii = removeList.length; i < ii; i++) {
              const clearMe = removeList[i];
              const p = clearMe.parentNode;
              p.removeChild(clearMe);
            }
          });
          return kml(xmldom);
        }
        return gpx(xmldom);
      });
  } else if (suffix === 'wkt') {
    return file
      .text()
      .then(text => WktParse(text))
      .then(geometry => ({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry,
          },
        ],
      }));
  }
  return new Promise((resolve, reject) => {
    reject(new Error(ERRORS.UNRECOGNIZED));
  });
}

function extractZip(zipfile) {
  return new Promise((resolve, reject) => {
    zipfile
      .arrayBuffer()
      .then(bytes => JSZip.loadAsync(bytes))
      .then(zip => {
        const files = [];
        zip.forEach((path, file) => {
          const fakeFile = {
            name: path,
            text: () => {
              return file.async('text');
            },
            arrayBuffer: () => {
              return file.async('uint8array');
            },
          };
          files.push(fakeFile);
        });
        resolve(files);
      })
      .catch(err => reject(err));
  });
}

export function countDatasets(files) {
  let count = 0;
  for (let i = 0, ii = files.length; i < ii; i++) {
    const ext = getExtension(files[i].name);
    if (DATASET_EXTENSIONS.indexOf(ext) >= 0) {
      count++;
    }
  }
  return count;
}

export function getFirstDataset(files) {
  for (let i = 0, ii = files.length; i < ii; i++) {
    const ext = getExtension(files[i].name);
    if (DATASET_EXTENSIONS.indexOf(ext) >= 0) {
      return files[i];
    }
  }
  return null;
}

export function validateGeometries(collection) {
  return new Promise((resolve, reject) => {
    const features = collection.features.filter(f =>
      isGeometryValid(f.geometry)
    );
    if (features.length === 0) {
      reject(new Error(ERRORS.NODATA));
    } else {
      resolve({
        ...collection,
        features,
      });
    }
  });
}

function importFiles(files) {
  return new Promise((resolve, reject) => {
    let collectionPromise;

    const hasZip = hasFileWithSuffix(files, 'zip|kmz')[0];
    const datasetCount = countDatasets(files);

    // unroll the zip!
    if (hasZip) {
      collectionPromise = extractZip(hasZip).then(files => importFiles(files));
    } else {
      if (datasetCount > 0) {
        const first = getFirstDataset(files);
        if (getExtension(first.name) === 'shp') {
          collectionPromise = parseShapefile(files);
        } else {
          collectionPromise = singleFileParser(first);
        }
      } else {
        reject(new Error(ERRORS.NODATA));
      }
    }
    collectionPromise
      .then(validateGeometries)
      .then(collection => {
        // Add the statistics if they have not been
        //  injected yet.
        if (!collection.importer) {
          collection.importer = {
            datasetCount,
            geometryTypes: getGeometryTypes(collection),
          };
        }
        resolve(collection);
      })
      .catch(err => reject(err));
  });
}

export default importFiles;
